// src/hooks/useParkingTrips.js
import { useState, useCallback } from "react";
import { db } from "../firebase/firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
} from "firebase/firestore";

// ⭐ Constant airport keywords
const AIRPORT_KEYWORDS = [
  "airport",
  "dxb",
  "dubai airport",
  "dubai international airport",
  "terminal 1",
  "t1",
  "terminal 2",
  "t2",
  "terminal 3",
  "t3",
  "arrivals",
  "arrival",
  "departures",
  "departure",
  "terminal",
];

export function useParkingTrips({ clientId, batchDate, pageSize = 100 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState("pending");

  const loadTrips = useCallback(
    async (reset = true) => {
      if (!clientId || !batchDate) {
        alert("Select client and date first.");
        return;
      }

      setLoading(true);

      try {
        // ---------------------------------------------------------
        // ⭐ CASE 1: ALL CLIENTS
        // ---------------------------------------------------------
        if (clientId === "all_clients") {
          const tripImportsSnap = await getDocs(collection(db, "tripImports"));
          let combined = [];

          for (const imp of tripImportsSnap.docs) {
            const id = imp.id; // e.g. "koi_2025-11-18"

            // Only this date
            if (!id.endsWith(batchDate)) continue;

            const tripsRef = collection(db, `tripImports/${id}/trips`);

            let qRef;
            if (filter === "pending") {
              qRef = query(tripsRef, where("parkingDone", "==", false));
            } else if (filter === "done") {
              qRef = query(tripsRef, where("parkingDone", "==", true));
            } else {
              qRef = query(tripsRef, orderBy("createdAt", "asc"));
            }

            const tripsSnap = await getDocs(qRef);

            const data = tripsSnap.docs.map((d) => ({
  id: d.id,
  ...d.data(),

  // ⭐ Add batch info so saving works
  clientKey: id.split("_")[0],  // "koi"
  clientName: id.split("_")[0], // TEMP until lookup added
  realBatchKey: id,             // "koi_2025-11-18"
}));


            combined.push(...data);
          }

          // ⭐ Airport filter
          combined = combined.filter((trip) => {
            const pickup = (trip.pickup || "").toLowerCase();
            return AIRPORT_KEYWORDS.some((kw) => pickup.includes(kw));
          });

          // Sort by createdAt for nicer UI
          combined.sort(
            (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
          );

          setRows(combined);
          setHasMore(false);
          setLastDoc(null);
          setLoading(false);
          return;
        }

        // ---------------------------------------------------------
        // ⭐ CASE 2: SINGLE CLIENT (with pagination)
        // ---------------------------------------------------------
        const batchKey = `${clientId}_${batchDate}`;
        const baseRef = collection(db, `tripImports/${batchKey}/trips`);

        let qRef;

        if (filter === "pending") {
          qRef = query(baseRef, where("parkingDone", "==", false), limit(pageSize));
        } else if (filter === "done") {
          qRef = query(baseRef, where("parkingDone", "==", true), limit(pageSize));
        } else {
          qRef = query(baseRef, orderBy("createdAt", "asc"), limit(pageSize));
        }

        if (!reset && lastDoc && filter === "all") {
          qRef = query(
            baseRef,
            orderBy("createdAt", "asc"),
            startAfter(lastDoc),
            limit(pageSize)
          );
        }

        const snap = await getDocs(qRef);

        let data = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          clientKey: clientId,
          realBatchKey: batchKey,
        }));

        // ⭐ Airport filter
        data = data.filter((trip) => {
          const pickup = (trip.pickup || "").toLowerCase();
          return AIRPORT_KEYWORDS.some((kw) => pickup.includes(kw));
        });

        setRows((prev) => (reset ? data : [...prev, ...data]));
        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.docs.length === pageSize);
      } catch (err) {
        console.error("Error loading parking trips:", err);
        alert("Failed to load trips.");
      } finally {
        setLoading(false);
      }
    },
    [clientId, batchDate, filter, lastDoc, pageSize]
  );

  return {
    rows,
    setRows,
    loading,
    filter,
    setFilter,
    hasMore,
    loadTrips,
  };
}
